import { useUnit } from 'effector-react'
import { useMutation } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { displaySettingsService } from '@/modules/display-settings/service/display-settings-service.ts'
import type { AddColorBody } from '@/modules/display-settings/service/inbox-indication-color-service.ts'
import { inboxIndicationColorService } from '@/modules/display-settings/service/inbox-indication-color-service.ts'
import { DisplaySettingsFormSchema } from '@/modules/display-settings/ui/validation.ts'
import { handleError } from '@/utils/error'

export const useDisplaySettingsMutations = () => {
  const creatorId = useUnit($creatorId)

  const countdownAudio = useMutation(
    'set-countdown-audio',
    (audio: string) =>
      displaySettingsService.setCountdownAudioFile({
        creatorId: creatorId!,
        sound: audio,
      }),
    { onError: (error) => handleError(error, 'Failed to set countdown audio') },
  )

  const countdownAudioVolume = useMutation(
    'set-countdown-volume',
    (volume: number) =>
      displaySettingsService.setCountdownAudioVolume({
        creatorId: creatorId!,
        volume: volume,
      }),
    {
      onError: (error) => handleError(error, 'Failed to set countdown volume'),
    },
  )

  const toggleEmojisMutation = useMutation(
    'toggle-emojis',
    (isToggled: boolean) =>
      displaySettingsService.toggleEmojiTaskbar({
        creatorId: creatorId!,
        toggleState: isToggled,
      }),
    { onError: (error) => handleError(error, 'Failed to toggle emojis') },
  )

  const setEmojisMutation = useMutation(
    'set-emojis',
    (emojis: string) => {
      DisplaySettingsFormSchema.shape.emojis.parse(emojis)

      return displaySettingsService.setEmojiTaskbar({
        creatorId: creatorId!,
        emojis: emojis,
      })
    },
    { onError: (error) => handleError(error, 'Failed to set emojis') },
  )

  const addColorMutation = useMutation(
    'add-spending-color',
    (color: AddColorBody) =>
      inboxIndicationColorService.addColor(creatorId!, color),
    { onError: (error) => handleError(error, 'Failed to add spending color') },
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editColorMutation = useMutation(
    'edit-spending-color',
    (color: any) => inboxIndicationColorService.editColor(color),
    { onError: (error) => handleError(error, 'Failed to edit spending color') },
  )

  const removeColorMutation = useMutation(
    'remove-spending-color',
    (color: number) =>
      inboxIndicationColorService.deleteColor(creatorId!, color),
    {
      onError: (error) => handleError(error, 'Failed to remove spending color'),
    },
  )

  const enableCustomPresetMutation = useMutation(
    'enable-custom-preset',
    (enabled: boolean) =>
      displaySettingsService.enableCustomNotePreset({
        creatorId: creatorId!,
        enabled: enabled,
      }),
    {
      onError: (error) => handleError(error, 'Failed to enable custom preset'),
    },
  )

  const setCustomPresetMutation = useMutation(
    'set-custom-preset',
    (note: string) =>
      displaySettingsService.setCustomNodePreset({
        creatorId: creatorId!,
        note,
      }),
    { onError: (error) => handleError(error, 'Failed to set custom preset') },
  )

  return {
    countdownAudio,
    countdownAudioVolume,
    toggleEmojisMutation,
    setEmojisMutation,
    addColorMutation,
    editColorMutation,
    removeColorMutation,
    enableCustomPresetMutation,
    setCustomPresetMutation,
  }
}
